/**
 * Rose degli altri — roster virtuale da auction_exits (non dalla Rosa propria).
 */

import { opponentPlaceholder } from './auctionOpponents.js';

/** Chip / stato: pile «Da assegnare» (opponent_id vuoto). */
export const ROSE_ALTRI_UNASSIGNED_ID = 'unassigned';

export function isUnassignedOpponentId(opponentId) {
  return opponentId == null || String(opponentId).trim() === '';
}

/**
 * @param {{ opponentId?: string|null }} entry
 * @param {string} selectedOpponentId
 */
export function exitMatchesSelectedOpponent(entry, selectedOpponentId) {
  const oid = entry?.opponentId ?? null;
  if (selectedOpponentId === ROSE_ALTRI_UNASSIGNED_ID) {
    return isUnassignedOpponentId(oid);
  }
  return String(oid || '') === String(selectedOpponentId || '');
}

/**
 * Costruisce una Map stile roster dai soli exit dell’avversario selezionato.
 * @param {Map<string, { price: number, at?: number, opponentId?: string|null }>|null|undefined} auctionExits
 * @param {string} selectedOpponentId
 * @returns {Map<string, { price: number, at?: number, opponentId?: string|null }>}
 */
export function buildOpponentRosterFromExits(auctionExits, selectedOpponentId) {
  const map = new Map();
  if (!(auctionExits instanceof Map)) return map;
  const selected = selectedOpponentId || ROSE_ALTRI_UNASSIGNED_ID;
  for (const [key, entry] of auctionExits.entries()) {
    if (!exitMatchesSelectedOpponent(entry, selected)) continue;
    map.set(key, {
      price: entry.price,
      at: entry.at,
      opponentId: entry.opponentId ?? null,
    });
  }
  return map;
}

/**
 * True se almeno un exit ha prezzo senza avversario (pile Da assegnare).
 * @param {Map<string, { opponentId?: string|null }>|null|undefined} auctionExits
 */
export function hasUnassignedExits(auctionExits) {
  if (!(auctionExits instanceof Map)) return false;
  for (const entry of auctionExits.values()) {
    if (isUnassignedOpponentId(entry?.opponentId)) return true;
  }
  return false;
}

/**
 * Chip: avversari, poi «Da assegnare» solo se ci sono exit senza avversario.
 * Mai la Rosa propria.
 * @param {{ id: string, name: string }[]|null|undefined} opponents
 * @param {{ auctionExits?: Map<string, { opponentId?: string|null }>|null }} [opts]
 * @returns {{ id: string, name: string, pile: boolean }[]}
 */
export function buildRoseAltriChipOptions(opponents, { auctionExits } = {}) {
  const chips = [];
  const list = Array.isArray(opponents) ? opponents : [];
  list.forEach((o, index) => {
    const id = String(o?.id || '').trim();
    if (!id) return;
    const rawName = String(o?.name ?? '').trim();
    chips.push({
      id,
      name: rawName || opponentPlaceholder(index),
      pile: false,
    });
  });
  if (hasUnassignedExits(auctionExits)) {
    chips.push({ id: ROSE_ALTRI_UNASSIGNED_ID, name: 'Da assegnare', pile: true });
  }
  return chips;
}

/**
 * Mantiene la selezione se ancora tra i chip; altrimenti primo chip disponibile.
 * @param {string|null|undefined} selected
 * @param {{ id: string, name: string }[]|null|undefined} opponents
 * @param {{ auctionExits?: Map<string, { opponentId?: string|null }>|null }} [opts]
 */
export function resolveRoseAltriSelectedOpponentId(selected, opponents, { auctionExits } = {}) {
  const chips = buildRoseAltriChipOptions(opponents, { auctionExits });
  if (selected && chips.some((c) => c.id === String(selected))) {
    return String(selected);
  }
  return chips[0]?.id ?? ROSE_ALTRI_UNASSIGNED_ID;
}
